import React, { Suspense, useCallback, useMemo } from 'react';
import {
  BottomSheetContainer,
  createUseBottomSheetModal,
  Flex,
  RemoteImage,
  Spinner,
  ScrollView,
  Typography,
  useScreenSize,
} from '@banhala/ui-kit';
import { useOverviewQuery } from '../queries/useOverviewQuery';
import { bridge, isNotNil } from '../../../../../utils';
import { useOpenDeeplink } from '../../../../../hooks';
import type { GetOverviewResponse } from '../../../../../api';

const TITLE_MAP = {
  beauty: '뷰티 카테고리',
  shoppingmall: '쇼핑몰 카테고리',
  brand_department: '브랜드 카테고리',
};

type OverviewItem = GetOverviewResponse['icon_list'][number];

const CategoryView: React.FC<{ mode: string }> = props => {
  const { data: overviewQuery } = useOverviewQuery(props.mode);
  const { width: screenWidth } = useScreenSize();
  const { openDeeplink } = useOpenDeeplink();

  const itemWidth = useMemo(() => {
    return (screenWidth - 16 * 2) / 5;
  }, [screenWidth]);

  const onPressItem = useCallback(
    (item: OverviewItem) => {
      bridge.logAnalyticsEvent({
        name: 'CLICK_CATEGORY',
        analytics: item.logging?.analytics,
        ably_only_log: item.logging?.ably_only_log,
      });

      openDeeplink(item.deeplink);
    },
    [openDeeplink],
  );

  return (
    <ScrollView>
      <Flex.Row
        flexWrap={'wrap'}
        rowGap={16}
        padding={{ horizontal: 16, vertical: 12 }}
      >
        {overviewQuery.icon_list.map(item => (
          <Flex.Column
            key={item.sno}
            gap={4}
            alignItems={'center'}
            width={itemWidth}
            onPress={() => onPressItem(item)}
          >
            <RemoteImage
              uri={item.image_url}
              width={48}
              height={48}
              borderRadius={'xlarge'}
            />
            <Typography
              type={'subtitle4'}
              numberOfLines={1}
              textAlign={'center'}
            >
              {item.name}
            </Typography>
          </Flex.Column>
        ))}
      </Flex.Row>
    </ScrollView>
  );
};

export const useOverviewBottomSheet = createUseBottomSheetModal<
  void,
  { url: string }
>(({ param }) => {
  const { height: screenHeight } = useScreenSize();
  const mode = useMemo(() => {
    const match = param.url.match(/\/overview\/([^&?/]+)/);
    return match?.[1] as keyof typeof TITLE_MAP | undefined;
  }, [param.url]);

  const title = useMemo(() => {
    return mode ? TITLE_MAP[mode] : undefined;
  }, [mode]);

  const sheetHeight = useMemo(() => {
    const match = param.url.match(/[&?]height_ratio=(\d+)/);
    const ratio = match ? Number(match[1]) / 100 : 1;
    return screenHeight * Math.min(1, ratio);
  }, [param.url, screenHeight]);

  return (
    <BottomSheetContainer
      title={title}
      maxHeight={sheetHeight}
      showHandleBar
      hideBorder
    >
      <Flex height={screenHeight}>
        {isNotNil(mode) && (
          <Suspense
            fallback={
              <Flex.Center flex={1} height={screenHeight}>
                <Spinner />
              </Flex.Center>
            }
          >
            <CategoryView mode={mode} />
          </Suspense>
        )}
      </Flex>
    </BottomSheetContainer>
  );
});
